import React, { useState, useRef, useEffect, useCallback } from 'react';
import TextField from '@material-ui/core/TextField';
import FormHelperText from '@material-ui/core/FormHelperText';

import { uuid, truncateDecimals } from '../../../utils/functions';

import './InputField.scss';

function normalizeInput(value) {
  if (value === null || value === undefined) {
    return '';
  }
  return value;
}

function InputField(props) {
  const {
    t,
    type,
    value,
    error,
    disabled,
    useEvent,
    classes: propsClasses,
    onChange,
    inputRef,
    InputProps,
    inputValidation,
    onValidationError,
    maxDecimalsAllowed,
    maxCharactersAllowed,
    required,
    name,
    label,
    minRows,
    maxRows,
    placeholder,
    defaultValue,
    multiline,
    margin,
    select,
    fullWidth,
    variant,
    autoComplete,
    autoFocus,
    onKeyDown,
    SelectProps,
    onFocus,
    onBlur,
    children
  } = props;

  // Blur when user scrolls the page
  const ref = useRef(inputRef);

  useEffect(() => {
    const myRef = ref;
    if (ref.current && ref.current.addEventListener) {
      const onMousewheel = () => {
        myRef.current.blur();
      };
      ref.current.addEventListener('mousewheel', onMousewheel, { passive: true });
      return () => myRef.current.removeEventListener('mousewheel', onMousewheel);
    }
    return undefined;
  }, []);

  // Apply custom classes
  const classes = ['threepp-input-field', type];
  if (propsClasses) {
    classes.push(propsClasses);
  }

  // Prevent null or undefined input
  const normalizedInput = normalizeInput(value);

  // Init state
  const [input, setInput] = useState(normalizedInput);

  // Init character count
  const [characterCount, setCharacterCount] = useState(normalizedInput.length);

  // Init error
  const [errorMsg, setError] = useState(error);

  // Show new input when value prop is updated
  useEffect(() => {
    setInput(normalizedInput);
    setCharacterCount(normalizedInput.length);
  }, [normalizedInput]);

  // Update error if error prop changes
  useEffect(() => {
    setError(error);
  }, [error]);
  const onInput = useCallback(
    event => {
      let enforcedValue = event.target.value;

      // Enforce max allowed decimals
      enforcedValue = truncateDecimals(enforcedValue, maxDecimalsAllowed);

      // Some components require original event structure (event.target.value)
      const callbackValue = useEvent ? { ...event, target: { ...event.target, value: enforcedValue } } : enforcedValue;

      // Invoke callbacks
      const errorObj = inputValidation(enforcedValue);
      if (errorObj) {
        onValidationError({ value: enforcedValue, error: errorObj });
      } else {
        onChange(callbackValue);
      }

      setInput(enforcedValue);
      setError(errorObj ? t(errorObj.key, { ...errorObj.args }) : null);
      setCharacterCount(enforcedValue.length);
    },
    [t, maxDecimalsAllowed, useEvent, inputValidation, onChange, onValidationError]
  );

  // Use inputProps maxLength to control max characters
  // This allows graceful handling of copy/paste and cursor jump issues
  let updatedInputProps = InputProps;
  if (maxCharactersAllowed) {
    const IP = InputProps || {};
    const ip = IP.inputProps || {};
    updatedInputProps = {
      ...IP,
      inputProps: {
        ...ip,
        maxLength: maxCharactersAllowed
      }
    };
  }
  if (type === 'date') {
    const IP = InputProps || {};
    const ip = IP.inputProps || {};
    updatedInputProps = {
      ...IP,
      inputProps: {
        ...ip,
        min: '0000-00-00',
        max: '9999-12-31'
      }
    };
  }

  return (
    <>
      <TextField
        inputRef={ref}
        className={classes.join(' ')}
        error={!!errorMsg}
        id={uuid()}
        required={required}
        name={name}
        label={label}
        type={type}
        minRows={minRows}
        maxRows={maxRows}
        placeholder={placeholder}
        defaultValue={defaultValue}
        multiline={multiline}
        margin={margin}
        select={select}
        fullWidth={fullWidth}
        variant={variant || 'filled'}
        autoComplete={autoComplete || 'off'}
        autoFocus={autoFocus}
        onKeyDown={onKeyDown}
        SelectProps={SelectProps}
        onFocus={onFocus}
        onChange={onInput}
        onBlur={onBlur}
        InputProps={updatedInputProps}
        disabled={disabled}
        value={input}
        InputLabelProps={{
          className: 'threepp-input-label'
        }}
      >
        {children}
      </TextField>
      <div className="threepp-input-field-helper-text-section">
        {errorMsg && (
          <FormHelperText className="threepp-input-error" error>
            {errorMsg}
          </FormHelperText>
        )}
        {maxCharactersAllowed && (
          <FormHelperText className="threepp-input-char-count">{`${characterCount} / ${maxCharactersAllowed}`}</FormHelperText>
        )}
      </div>
    </>
  );
}

InputField.defaultProps = {
  type: 'text',
  value: '',
  disabled: false,
  t: k => k,
  inputValidation: () => null,
  onValidationError: e => e
};

export default InputField;
